import React from 'react';

import {
  TpHoverInfo,
  TpHoverRenderContext,
  TpHoverRenderInfo,
  ZoomPanCanvas,
} from '../../zoompan-canvas/ZoomPanCanvas';
import { TpZoomPanelInvPointMapInfo } from '../../zoompan-canvas/zoomPanCanvasHelpers';
import { TpChartProps } from '../interfaces';
import { createDefinition } from './stackedCompositionPlot.Renderer';
import { TpRectangleSelection } from '../../zoompan-canvas/helpers';
import { convertColumnDataToDisplayString } from '../../helpers/misc';

export function StackedCompositionPlot({
  chartTypeManifest,
  dataContext,
  dataBinding,
  config,
  widgetContext,
}: TpChartProps) {
  const widgetGlobalConfig = widgetContext.dashboardContext.widgetGlobalConfig ?? {};
  const {
    areaStacks,
    definition,
    matrixColumns,
    rowCompositionGroupColInfo,
    dataHoverLabelcolumn,
    dataHoverLabelcolumnInfo,
    colSortIndex,
    compositionGroupInfo,
    plotRelative,
    columnCanonicalTypeInfo,
    dataColItemIds,
    title,
  } = createDefinition(chartTypeManifest, dataContext, dataBinding, config, widgetGlobalConfig);

  const colCount = matrixColumns.length;

  function getHoverInfo(
    mappingInfo: TpZoomPanelInvPointMapInfo,
    logicPositX: number,
    logicPositY: number,
    renderPositX: number,
    renderPositY: number,
  ): TpHoverInfo | null {
    if (mappingInfo.area?.id != 'stacks') return null;

    const colIdxUnsorted = Math.floor(logicPositX * colCount);
    if (colIdxUnsorted < 0 || colIdxUnsorted >= colCount) return null;
    const colIdx = colSortIndex[colIdxUnsorted];

    if (compositionGroupInfo.columnTotalAbundances[colIdx] <= 0) return null;

    const groupAbundances = compositionGroupInfo.columnsGroupAbundances[colIdx];
    let stackedAbundance = 0;
    const relativeFactor = plotRelative ? 1 / compositionGroupInfo.columnTotalAbundances[colIdx] : 1;
    let hoverGroupNr = -1;
    let logicHoverPosY = 0;
    for (let groupNr = 0; groupNr < compositionGroupInfo.compositionGroups.length; groupNr++) {
      const groupInfo = compositionGroupInfo.compositionGroups[groupNr];
      const stackedAbundanceEnd = stackedAbundance + groupAbundances[groupNr];
      if (logicPositY > relativeFactor * stackedAbundance && logicPositY <= relativeFactor * stackedAbundanceEnd) {
        hoverGroupNr = groupNr;
        logicHoverPosY = (relativeFactor * (stackedAbundance + stackedAbundanceEnd)) / 2;
      }
      stackedAbundance = stackedAbundanceEnd;
    }
    if (hoverGroupNr < 0) return null;

    return {
      dataTableId: dataBinding.dataTableId,
      itemId: {
        colIdx,
        hoverGroupNr,
      },
      custom: {
        colIdxUnsorted,
        rowCompositionGroupId: rowCompositionGroupColInfo.id,
        logicHoverPosY,
        groupName: compositionGroupInfo.compositionGroups[hoverGroupNr].groupName,
        groupAbundance: groupAbundances[hoverGroupNr],
        groupAbundanceRel: groupAbundances[hoverGroupNr] / compositionGroupInfo.columnTotalAbundances[colIdx],
      },
    };
  }

  function getHoverRenderInfo(hoverInfo: TpHoverInfo, context: TpHoverRenderContext): TpHoverRenderInfo | null {
    if (hoverInfo.dataTableId != dataBinding.dataTableId) return null;
    if (!hoverInfo.custom || hoverInfo.custom.rowCompositionGroupId != rowCompositionGroupColInfo.id) return null;

    const labels: string[] = [
      convertColumnDataToDisplayString(dataHoverLabelcolumnInfo, dataHoverLabelcolumn[hoverInfo.itemId.colIdx]),
      hoverInfo.custom.groupName,
      `Abundance: ${hoverInfo.custom.groupAbundance} (${(100 * hoverInfo.custom.groupAbundanceRel).toFixed(2)}%)`,
    ];
    return {
      area: areaStacks,
      logicPosX: (hoverInfo.custom.colIdxUnsorted + 0.5) / colCount,
      logicPosY: hoverInfo.custom.logicHoverPosY,
      labels,
    };
  }

  function onRectangleSelection(rectangleSelection: TpRectangleSelection) {}

  function onItemClicked(itemId: any) {
    widgetContext.handleOpenItem(columnCanonicalTypeInfo.id, dataColItemIds[itemId.colIdx]);
  }

  return (
    <div style={{ width: '100%', height: '100%' }}>
      <ZoomPanCanvas
        definition={definition}
        getHoverInfo={getHoverInfo}
        getHoverRenderInfo={getHoverRenderInfo}
        onRectangleSelection={onRectangleSelection}
        onItemClicked={onItemClicked}
        maxZoomXAbsolute={colCount * 20}
        maxZoomYRelative={30}
        widgetContext={widgetContext}
        chartTitleInfo={{
          assetId: dataContext.assetId,
          dataType: chartTypeManifest.id,
          title,
        }}
      />
    </div>
  );
}
